<template>
  <div class="problematic-kits-wrapper">
    <!-- Top Filter Controls Card -->
    <div class="controls-card">
      <div class="controls-row">
        <div class="controls-left-group">
          <div class="filter-group-wrap">
            <label>Date Range:</label>
            <select v-model="activePreset" @change="selectPreset(activePreset)">
              <option value="7days">Last 7 Days</option>
              <option value="month">This Month</option>
              <option value="30days">Last 30 Days</option>
              <option value="60days">Last 60 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom" v-if="activePreset === 'custom'">Custom Range</option>
            </select>
          </div>

          <div class="filter-group-wrap">
            <label>Kit Type:</label>
            <select v-model="selectedPaymentFilter" @change="fetchProblematicKits">
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="free">Promo</option>
            </select>
          </div>
        </div>

        <div class="date-picker-group">
          <div class="date-input-wrap">
            <label>From:</label>
            <input
              type="date"
              v-model="dateFrom"
              @change="onCustomDateChange"
            />
          </div>
          <div class="date-input-wrap">
            <label>To:</label>
            <input type="date" v-model="dateTo" @change="onCustomDateChange" />
          </div>
          <button class="apply-btn" @click="fetchProblematicKits">
            Apply Filter
          </button>
          <button class="reset-btn" @click="resetFilters">Reset</button>
        </div>
      </div>

      <div class="search-filter-row">
        <div class="search-wrap">
          <input
            type="text"
            v-model="searchQuery"
            @keyup.enter="fetchProblematicKits"
            placeholder="Search by Kit ID, Customer Name, or Email..."
          />
          <button class="search-btn" @click="fetchProblematicKits">
            Search
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table Card -->
    <div class="table-card">
      <div class="table-header-bar">
        <div class="table-header-title">
          <h3>Problematic Kits List</h3>
          <span class="total-count-badge">
            Total Completed Kits: {{ totalCompletedKits }} | Problematic Kits: {{ totalProblematicKits }} | Problematic Rate: {{ problematicRate }}%
          </span>
        </div>
        <div class="table-controls-right">
          <button class="export-btn" @click="exportCSV" :disabled="isLoading">
            <img
              src="@/images/dashboard/download.svg"
              alt="Export"
              class="btn-icon"
            />
            Export CSV
          </button>
          <div class="reload-wrap">
            <img
              :src="
                isLoading
                  ? require('@/images/dashboard/reloading-gif.svg')
                  : require('@/images/dashboard/refresh.png')
              "
              @click="fetchProblematicKits"
              class="reload-icon"
              title="Refresh List"
            />
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="loading-state">
        <p>Loading problematic kits data...</p>
      </div>

      <div v-else-if="sortedKits.length === 0" class="no-data-state">
        <p>
          No problematic kits found for the selected date range and filters.
        </p>
      </div>

      <div v-else class="table-container">
        <table class="styled-table">
          <thead>
            <tr>
              <th class="title-data" @click="toggleSort('id')">
                KIT ID
                <span class="sort-data">
                  <img class="img-sort" src="@/images/dashboard/up.png" alt="#" v-if="sortByColumn === 'id' && sortDirection === 'desc'" />
                  <img class="img-sort down" src="@/images/dashboard/up.png" alt="#" v-else-if="sortByColumn === 'id' && sortDirection === 'asc'" />
                  <span v-else class="sort-arrow-placeholder">↕</span>
                </span>
              </th>
              <th class="title-data" @click="toggleSort('customer_name')">
                CUSTOMER NAME
                <span class="sort-data">
                  <img class="img-sort" src="@/images/dashboard/up.png" alt="#" v-if="sortByColumn === 'customer_name' && sortDirection === 'desc'" />
                  <img class="img-sort down" src="@/images/dashboard/up.png" alt="#" v-else-if="sortByColumn === 'customer_name' && sortDirection === 'asc'" />
                  <span v-else class="sort-arrow-placeholder">↕</span>
                </span>
              </th>
              <th class="title-data" @click="toggleSort('email')">
                EMAIL
                <span class="sort-data">
                  <img class="img-sort" src="@/images/dashboard/up.png" alt="#" v-if="sortByColumn === 'email' && sortDirection === 'desc'" />
                  <img class="img-sort down" src="@/images/dashboard/up.png" alt="#" v-else-if="sortByColumn === 'email' && sortDirection === 'asc'" />
                  <span v-else class="sort-arrow-placeholder">↕</span>
                </span>
              </th>
              <th class="title-data" @click="toggleSort('reported_at')">
                REPORTED AT
                <span class="sort-data">
                  <img class="img-sort" src="@/images/dashboard/up.png" alt="#" v-if="sortByColumn === 'reported_at' && sortDirection === 'desc'" />
                  <img class="img-sort down" src="@/images/dashboard/up.png" alt="#" v-else-if="sortByColumn === 'reported_at' && sortDirection === 'asc'" />
                  <span v-else class="sort-arrow-placeholder">↕</span>
                </span>
              </th>
              <th class="title-data" @click="toggleSort('problem')">
                PROBLEM
                <span class="sort-data">
                  <img class="img-sort" src="@/images/dashboard/up.png" alt="#" v-if="sortByColumn === 'problem' && sortDirection === 'desc'" />
                  <img class="img-sort down" src="@/images/dashboard/up.png" alt="#" v-else-if="sortByColumn === 'problem' && sortDirection === 'asc'" />
                  <span v-else class="sort-arrow-placeholder">↕</span>
                </span>
              </th>
              <th class="title-data" @click="toggleSort('kit_status')">
                STATUS
                <span class="sort-data">
                  <img class="img-sort" src="@/images/dashboard/up.png" alt="#" v-if="sortByColumn === 'kit_status' && sortDirection === 'desc'" />
                  <img class="img-sort down" src="@/images/dashboard/up.png" alt="#" v-else-if="sortByColumn === 'kit_status' && sortDirection === 'asc'" />
                  <span v-else class="sort-arrow-placeholder">↕</span>
                </span>
              </th>
              <th class="title-data text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="kit in sortedKits" :key="kit.id">
              <td class="kit-desc clickable" @click="openKitDetail(kit)" title="Click to view kit details">
                <strong class="clickable-kit-id">{{ convertKitId(kit.kit_id || kit.id) }}</strong>
              </td>
              <td class="kit-desc">{{ getFullName(kit) }}</td>
              <td class="kit-desc">
                <span class="cus-email-cell">{{ getCustomerEmail(kit) }}</span>
              </td>
              <td class="kit-desc">{{ formatReportedAt(kit.reported_at) }}</td>
              <td class="kit-desc">
                <div class="problem-tag" :class="getProblemClass(kit)">
                  {{ getProblemReason(kit) }}
                </div>
              </td>
              <td class="kit-desc">
                <span class="status-badge" :class="getKitFailureStatusClass(kit)">
                  {{ getKitFailureStatus(kit) }}
                </span>
              </td>
              <td class="kit-desc text-center">
                <button class="action-btn" @click="openKitDetail(kit)">
                  View Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="totalPages > 1">
        <Paginate
          :page-count="totalPages"
          :page-class="'pogination-item'"
          :click-handler="handlePageChange"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pogination-wrap'"
        />
      </div>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import axios from "axios";
import Paginate from "vuejs-paginate";
import { mapActions } from "vuex";
import { parseAndConvertKitId } from "../mixins/parseAndConvertKitId";

export default {
  name: "ProblematicKits",
  mixins: [parseAndConvertKitId],
  components: {
    Paginate
  },
  data() {
    return {
      isLoading: false,
      kits: [],
      totalProblematicKits: 0,
      totalCompletedKits: 0,
      totalKitsCount: 0,
      currentPage: 1,
      totalPages: 1,
      searchQuery: "",
      selectedIssueFilter: "",
      selectedSourceFilter: "",
      selectedPaymentFilter: "",
      dateFrom: moment()
        .subtract(30, "days")
        .format("YYYY-MM-DD"),
      dateTo: moment().format("YYYY-MM-DD"),
      activePreset: "30days",
      presets: [
        { id: "7days", label: "Last 7 Days" },
        { id: "30days", label: "Last 30 Days" },
        { id: "60days", label: "Last 60 Days" },
        { id: "month", label: "This Month" },
        { id: "90days", label: "Last 90 Days" },
        { id: "all", label: "All Time" }
      ],
      issueBreakdown: {
        overloaded: 0,
        damaged: 0,
        missing: 0,
        blank: 0
      },
      selectedKitModal: null,
      sortByColumn: "color",
      sortDirection: "asc"
    };
  },
  computed: {
    safeTotalKits() {
      return this.totalCompletedKits || this.totalKitsCount || 0;
    },
    problematicRate() {
      if (!this.safeTotalKits || this.safeTotalKits <= 0) return 0;
      const rate = (this.totalProblematicKits / this.safeTotalKits) * 100;
      return parseFloat(rate.toFixed(1));
    },
    dateRangeLabel() {
      if (this.activePreset === "all" || (!this.dateFrom && !this.dateTo)) return "All Time";
      return `${moment(this.dateFrom).format("MMM D")} - ${moment(
        this.dateTo
      ).format("MMM D, YYYY")}`;
    },
    totalDaysCount() {
      if (!this.dateFrom || !this.dateTo) return "All";
      return moment(this.dateTo).diff(moment(this.dateFrom), "days") + 1;
    },
    sortedKits() {
      if (!this.kits || this.kits.length === 0) return [];
      let list = [...this.kits];

      const colorRankMap = {
        "badge-overloaded": 1, // Red
        "badge-damaged": 2,    // Orange
        "badge-missing": 3,    // Yellow
        "badge-blank": 4,      // Blue
        "badge-general": 5     // Grey
      };

      list.sort((a, b) => {
        let result = 0;
        if (this.sortByColumn === "color") {
          const rankA = colorRankMap[this.getProblemClass(a)] || 99;
          const rankB = colorRankMap[this.getProblemClass(b)] || 99;
          result = rankA - rankB;
        } else if (this.sortByColumn === "id") {
          const idA = (a.kit_id || a.id || "").toString();
          const idB = (b.kit_id || b.id || "").toString();
          result = idA.localeCompare(idB, undefined, { numeric: true });
        } else if (this.sortByColumn === "customer_name") {
          const nameA = this.getFullName(a).toLowerCase();
          const nameB = this.getFullName(b).toLowerCase();
          result = nameA.localeCompare(nameB);
        } else if (this.sortByColumn === "email") {
          const emailA = this.getCustomerEmail(a).toLowerCase();
          const emailB = this.getCustomerEmail(b).toLowerCase();
          result = emailA.localeCompare(emailB);
        } else if (this.sortByColumn === "reported_at") {
          const dateA = new Date(a.reported_at || 0).getTime();
          const dateB = new Date(b.reported_at || 0).getTime();
          result = dateA - dateB;
        } else if (this.sortByColumn === "date") {
          const dateA = new Date(a.created_at || a.shipped_at || 0).getTime();
          const dateB = new Date(b.created_at || b.shipped_at || 0).getTime();
          result = dateA - dateB;
        } else if (this.sortByColumn === "problem") {
          const probA = this.getProblemReason(a).toLowerCase();
          const probB = this.getProblemReason(b).toLowerCase();
          result = probA.localeCompare(probB);
        } else if (this.sortByColumn === "kit_status") {
          const stA = this.getKitFailureStatus(a);
          const stB = this.getKitFailureStatus(b);
          result = stA.localeCompare(stB);
        } else if (this.sortByColumn === "coupon") {
          const coupA = this.isCouponEligible(a) ? 1 : 0;
          const coupB = this.isCouponEligible(b) ? 1 : 0;
          result = coupA - coupB;
        }

        return this.sortDirection === "asc" ? result : -result;
      });

      return list;
    }
  },
  mounted() {
    this.fetchProblematicKits();
  },
  methods: {
    toggleSort(column) {
      if (this.sortByColumn === column) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortByColumn = column;
        this.sortDirection = "asc";
      }
    },
    ...mapActions(["setLoader", "testKitInfo", "getUserInfo"]),
    filterByIssueType(type) {
      if (this.selectedIssueFilter === type) {
        this.selectedIssueFilter = "";
      } else {
        this.selectedIssueFilter = type;
      }
      this.sortByColumn = "color";
      this.fetchProblematicKits();
    },
    selectPreset(presetId) {
      this.activePreset = presetId;
      const today = moment().format("YYYY-MM-DD");
      if (presetId === "7days") {
        this.dateFrom = moment()
          .subtract(7, "days")
          .format("YYYY-MM-DD");
        this.dateTo = today;
      } else if (presetId === "30days") {
        this.dateFrom = moment()
          .subtract(30, "days")
          .format("YYYY-MM-DD");
        this.dateTo = today;
      } else if (presetId === "60days") {
        this.dateFrom = moment()
          .subtract(60, "days")
          .format("YYYY-MM-DD");
        this.dateTo = today;
      } else if (presetId === "month") {
        this.dateFrom = moment()
          .startOf("month")
          .format("YYYY-MM-DD");
        this.dateTo = today;
      } else if (presetId === "90days") {
        this.dateFrom = moment()
          .subtract(90, "days")
          .format("YYYY-MM-DD");
        this.dateTo = today;
      } else if (presetId === "all") {
        this.dateFrom = "";
        this.dateTo = "";
      }
      this.currentPage = 1;
      this.fetchProblematicKits();
    },
    onCustomDateChange() {
      this.activePreset = "custom";
    },
    resetFilters() {
      this.searchQuery = "";
      this.selectedIssueFilter = "";
      this.selectedPaymentFilter = "";
      this.selectPreset("30days");
    },
    async fetchProblematicKits() {
      this.isLoading = true;
      try {
        let params = `problem=1&per_page=20&page=${this.currentPage}`;
        if (this.activePreset === "30days") {
          params += `&days=30`;
        } else if (this.activePreset === "60days") {
          params += `&days=60`;
        } else if (this.activePreset === "7days") {
          params += `&days=7`;
        } else if (this.activePreset === "90days") {
          params += `&days=90`;
        } else {
          if (this.dateFrom) params += `&date_from=${this.dateFrom}`;
          if (this.dateTo) params += `&date_to=${this.dateTo}`;
        }
        if (this.searchQuery)
          params += `&search=${encodeURIComponent(this.searchQuery)}`;
        if (this.selectedPaymentFilter)
          params += `&payment=${this.selectedPaymentFilter}`;
        if (this.selectedIssueFilter) {
          let sub = this.selectedIssueFilter;
          if (sub === "overloaded" || sub === "1") sub = 1;
          else if (sub === "missing" || sub === "2") sub = 2;
          params += `&overloadSub=${sub}`;
        }

        const response = await axios.get(`/v1/admin/problematicKits?${params}`);
        const data = response.data.data;
        const kitList = data.testkits ? data.testkits.data : [];
        this.kits = kitList;
        const problematicTotal = (data.sub_total_count !== undefined && data.sub_total_count !== null)
          ? data.sub_total_count
          : (data.total !== undefined ? data.total : kitList.length);
        this.totalProblematicKits = problematicTotal;
        const completedTotal = (data.total_completed_kits !== undefined && data.total_completed_kits !== null)
          ? data.total_completed_kits
          : ((data.total_kits_count !== undefined && data.total_kits_count !== null)
            ? data.total_kits_count
            : 0);
        this.totalCompletedKits = completedTotal;
        this.totalKitsCount = completedTotal;
        this.totalPages = data.testkits ? data.testkits.lastPage : 1;

        if (data.issue_breakdown) {
          this.issueBreakdown = data.issue_breakdown;
          this.retestCount = data.retest_count !== undefined ? data.retest_count : kitList.length;
        } else {
          this.calculateBreakdown(kitList, problematicTotal);
        }
      } catch (err) {
        console.error("Error fetching problematic kits:", err);
        this.kits = [];
        this.totalProblematicKits = 0;
        this.totalCompletedKits = 0;
        this.totalKitsCount = 0;
      } finally {
        this.isLoading = false;
      }
    },
    isAmazonKit(kit) {
      if (!kit) return false;
      if (kit.amazon_kits == 1 || kit.amazon_kits === "1") return true;
      if (kit.collected_email && kit.collected_email.toLowerCase().includes("amazon@gotmold.com")) return true;
      return false;
    },
    calculateBreakdown(kitList, total) {
      let overloaded = 0;
      let damaged = 0;
      let missing = 0;
      let blank = 0;
      let retests = 0;

      kitList.forEach(kit => {
        const reason = (this.getProblemReason(kit) || "").toLowerCase();
        const isOverloaded = kit.overloaded == 1 || reason.includes("overload") || reason.includes("dust") || reason.includes("debris");
        const isMissing = kit.missing_outdoor == 1 || reason.includes("miss") || reason.includes("lost");
        const isDamaged = kit.damaged == 1 || reason.includes("damage") || reason.includes("defect") || reason.includes("broken") || reason.includes("manufactur");
        const isBlank = kit.blank == 1 || reason.includes("blank") || reason.includes("empty") || reason.includes("seal") || reason.includes("pump");

        if (isOverloaded) {
          overloaded++;
        } else if (isMissing) {
          missing++;
        } else if (isDamaged) {
          damaged++;
        } else if (isBlank) {
          blank++;
        } else {
          // Default classification when general problematic kit is returned
          if (kit.overloaded !== undefined && kit.overloaded == 1) overloaded++;
          else if (kit.missing_outdoor !== undefined && kit.missing_outdoor == 1) missing++;
          else overloaded++;
        }
      });

      this.issueBreakdown = { overloaded, damaged, missing, blank };
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchProblematicKits();
    },
    getFirstName(kit) {
      if (kit.user && kit.user.first_name) return kit.user.first_name;
      if (kit.collected_first_name) return kit.collected_first_name;
      const name = this.getCustomerName(kit);
      if (name && name !== "N/A") {
        return name.split(" ")[0] || "N/A";
      }
      return "N/A";
    },
    getLastName(kit) {
      if (kit.user && kit.user.last_name) return kit.user.last_name;
      if (kit.collected_last_name) return kit.collected_last_name;
      const name = this.getCustomerName(kit);
      if (name && name !== "N/A") {
        const parts = name.split(" ");
        return parts.length > 1 ? parts.slice(1).join(" ") : "N/A";
      }
      return "N/A";
    },
    getFullName(kit) {
      const first = this.getFirstName(kit);
      const last = this.getLastName(kit);
      if (first === "N/A" && last === "N/A") return "N/A";
      if (first === "N/A") return last;
      if (last === "N/A") return first;
      return (first + " " + last).trim();
    },
    getCustomerName(kit) {
      if (kit.user) {
        const fname = kit.user.first_name || "";
        const lname = kit.user.last_name || "";
        return (fname + " " + lname).trim() || kit.user.name || "N/A";
      }
      return kit.customer_name || "N/A";
    },
    getKitFailureStatus(kit) {
      const status = kit.status;
      // status 11 = partially_failed, status 10 = analysis_failed (completely failed)
      if (status == 11 || status === "partially_failed") return "Partially Failed";
      if (status == 10 || status === "analysis_failed") return "Analysis Failed";
      // Fall back to inspecting problem text for clues
      const reason = (this.getProblemReason(kit) || "").toLowerCase();
      if (reason.includes("partially failed") || reason.includes("partial")) return "Partially Failed";
      if (reason.includes("analysis failed") || reason.includes("completely failed")) return "Analysis Failed";
      // Generic problematic kit without a specific failure status
      return "Problematic";
    },
    getKitFailureStatusClass(kit) {
      const s = this.getKitFailureStatus(kit);
      if (s === "Partially Failed") return "status-partial";
      if (s === "Analysis Failed") return "status-complete";
      return "status-general";
    },
    getCustomerEmail(kit) {
      if (kit.user && kit.user.email) return kit.user.email;
      if (kit.collected_email) return kit.collected_email;
      return kit.email || "N/A";
    },
    getProblemReason(kit) {
      const problemList = kit.kitProblems || kit.problems || [];
      if (problemList && problemList.length > 0) {
        const seen = new Set();
        const unique = [];
        problemList.forEach(p => {
          const label = (p.problem || p.comment || "").trim();
          const key = label.toLowerCase();
          if (label && !seen.has(key)) {
            seen.add(key);
            unique.push(label);
          }
        });
        return unique.join(", ");
      }
      if (kit.problem_comment) return kit.problem_comment;
      if (kit.comment) return kit.comment;
      if (kit.overloaded == 1) return "Overloaded Sample (Excessive dust/debris)";
      if (kit.missing_outdoor == 1) return "Missing Outdoor Cassette";
      if (kit.status == 6 || kit.status === "problem") return "Problematic Kit - Retest Required";
      if (kit.status == 10 || kit.status === "analysis_failed") return "Analysis Failed - Retest Required";
      if (kit.status == 11 || kit.status === "partially_failed") return "Analysis Partially Failed";
      return "Problematic Kit";
    },
    isCouponEligible(kit) {
      const problemList = kit.kitProblems || kit.problems || [];
      if (problemList && problemList.length > 0) {
        return problemList.some(p => {
          const prob = (p.problem || p.comment || "").toLowerCase();
          return ["overloaded", "damaged", "defective", "missing", "blank"].some(k => prob.includes(k));
        });
      }
      return kit.overloaded == 1 || kit.missing_outdoor == 1;
    },
    getProblemClass(kit) {
      const reason = (this.getProblemReason(kit) || "").toLowerCase();
      if (reason.includes("overload")) return "badge-overloaded";
      if (reason.includes("damage")) return "badge-damaged";
      if (reason.includes("miss")) return "badge-missing";
      if (reason.includes("blank")) return "badge-blank";
      return "badge-general";
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      return moment(dateStr).format("MMM D, YYYY h:mm A");
    },
    formatReportedAt(dateStr) {
      if (!dateStr) return "N/A";
      return moment(dateStr).format("MMM D, YYYY h:mm A");
    },
    formatDateOnly(dateStr) {
      if (!dateStr) return "N/A";
      return moment(dateStr).format("MMM D, YYYY");
    },
    openKitDetail(kit) {
      const kitId = kit.id || kit.kit_id;
      const userId = kit.user_id || (kit.user ? kit.user.id : null);

      if (!kitId) return;

      this.testKitInfo(kitId).then(() => {
        if (userId !== null && userId !== undefined) {
          this.getUserInfo(userId).then(() => {
            this.$emit("showModal", true);
          });
        } else {
          this.$emit("showModal", true);
        }
      }).catch(err => {
        console.error("Error fetching kit details:", err);
        this.$emit("showModal", true);
      });
    },
    exportCSV() {
      if (!this.sortedKits || this.sortedKits.length === 0) {
        alert("No problematic kits available to export for the selected filters.");
        return;
      }

      const headers = [
        "Kit ID",
        "Customer Name",
        "Email",
        "Reported At",
        "Problem",
        "Status"
      ];

      const rows = this.sortedKits.map(kit => {
        const rawId = kit.kit_id || kit.id || "";
        const kitId = this.convertKitId(rawId) || rawId;
        const customerName = this.getFullName(kit);
        const email = this.getCustomerEmail(kit);
        const reportedAt = this.formatReportedAt(kit.reported_at);
        const problem = this.getProblemReason(kit);
        const status = this.getKitFailureStatus(kit);

        return [
          `"${kitId.toString().replace(/"/g, '""')}"`,
          `"${customerName.toString().replace(/"/g, '""')}"`,
          `"${email.toString().replace(/"/g, '""')}"`,
          `"${reportedAt.toString().replace(/"/g, '""')}"`,
          `"${problem.toString().replace(/"/g, '""')}"`,
          `"${status.toString().replace(/"/g, '""')}"`
        ].join(",");
      });

      const csvContent = "\uFEFF" + [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `ProblematicKitsReport_${moment().format("YYYYMMDD_HHmmss")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.problematic-kits-wrapper {
  padding: 20px;
  margin-top: 10px;
  background-color: #f7f9fc;
  min-height: 80vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}

.header-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
}

.title-section h2 {
  margin: 0 0 6px 0;
  color: #005eb8;
  font-size: 24px;
  font-weight: 600;
}

.title-section .description {
  margin: 0;
  color: #666666;
  font-size: 14px;
}

.export-btn {
  background-color: #005eb8;
  color: #ffffff;
  border: none;
  padding: 10px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.export-btn:hover {
  background-color: #004488;
}

.export-btn img,
.btn-icon {
  width: 16px;
  height: 16px;
  max-width: 16px;
  max-height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.controls-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
}

.controls-left-group {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.preset-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preset-group .label {
  font-weight: 500;
  color: #444;
  margin-right: 4px;
}

.preset-btn {
  background: #f0f4f8;
  border: 1px solid #d0dbe5;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #444;
  transition: all 0.2s;
}

.preset-btn.active,
.preset-btn:hover {
  background: #005eb8;
  color: #ffffff;
  border-color: #005eb8;
}

.date-picker-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
}

.date-input-wrap input {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.apply-btn {
  background: #28a745;
  color: #ffffff;
  border: none;
  padding: 7px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.reset-btn {
  background: #6c757d;
  color: #ffffff;
  border: none;
  padding: 7px 14px;
  border-radius: 4px;
  cursor: pointer;
}

.search-filter-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.search-wrap {
  display: flex;
  flex: 1;
  gap: 8px;
}

.search-wrap input {
  flex: 1;
  padding: 8px 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.search-btn {
  background: #005eb8;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.filter-group-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.filter-group-wrap label {
  font-weight: 500;
  color: #555;
  white-space: nowrap;
}

.filter-group-wrap select {
  padding: 8px 30px 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background-color: #ffffff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%3C005eb8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  font-size: 13px;
  color: #1e293b;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.filter-group-wrap select:focus {
  border-color: #005eb8;
  box-shadow: 0 0 0 3px rgba(0, 94, 184, 0.15);
}

.issue-filter-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.issue-filter-wrap select {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.metric-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #005eb8;
}

.metric-card.highlight-green {
  border-left-color: #28a745;
}
.metric-card.highlight-orange {
  border-left-color: #fd7e14;
}
.metric-card.highlight-purple {
  border-left-color: #6f42c1;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.metric-title {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
}

.metric-badge {
  font-size: 11px;
  background: #eef2f7;
  color: #444;
  padding: 2px 6px;
  border-radius: 4px;
}

.metric-value {
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
}

.date-range-display {
  font-size: 16px;
  font-weight: 600;
  padding-top: 6px;
}

.metric-sub {
  font-size: 12px;
  color: #888;
}

.breakdown-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.breakdown-card h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
}

.breakdown-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.breakdown-icon.overloaded {
  background: #dc3545;
}
.breakdown-icon.damaged {
  background: #fd7e14;
}
.breakdown-icon.missing {
  background: #ffc107;
}
.breakdown-icon.blank {
  background: #17a2b8;
}

.breakdown-info {
  display: flex;
  flex-direction: column;
}

.breakdown-count {
  font-size: 18px;
  font-weight: 700;
  color: #222;
}

.breakdown-label {
  font-size: 12px;
  color: #666;
}

.table-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.table-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.total-count-badge {
  background: #e0f2fe;
  color: #0284c7;
  font-weight: 700;
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #bae6fd;
}

.table-controls-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-header-bar h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.reload-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.table-container {
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  font-size: 14px;
}

.styled-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.9em;
  text-align: left;
}

.styled-table thead tr {
  background-color: #005eb8;
  color: #ffffff;
  text-align: left;
  height: 37px;
}

.styled-table th {
  padding: 12px 15px;
  border-bottom: 1px solid #dddddd;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  color: #ffffff;
}

.styled-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #dddddd;
  vertical-align: middle;
}

.styled-table tbody tr {
  border-bottom: 1px solid #dddddd;
}

.styled-table tbody tr:nth-of-type(even) {
  background-color: #f3f3f3;
}

.styled-table tbody tr:last-of-type {
  border-bottom: 2px solid #005eb8;
}

.styled-table tbody tr:hover {
  background-color: #e0e9f4;
}

.sort-data {
  padding-left: 6px;
  display: inline-block;
}

.sort-data .down {
  transform: rotate(180deg);
}

.img-sort {
  width: 10px;
  height: auto;
  vertical-align: middle;
}

.text-center {
  text-align: center;
}

.clickable-kit-id {
  color: #005eb8;
  cursor: pointer;
}

.clickable-kit-id:hover {
  color: #003e7a;
  text-decoration: underline;
}

.customer-info {
  display: flex;
  flex-direction: column;
}

.cus-name {
  font-weight: 500;
  color: #1e293b;
}

.cus-email {
  font-size: 12px;
  color: #64748b;
}

.cus-email-cell {
  font-size: 13px;
  color: #475569;
}

.coupon-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.coupon-badge.eligible {
  background-color: #dcfce7;
  color: #15803d;
}

.coupon-badge.ineligible {
  background-color: #f1f5f9;
  color: #64748b;
}

.problem-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: #e2e8f0;
  color: #334155;
}

.badge-overloaded {
  background: #fee2e2;
  color: #991b1b;
}
.badge-damaged {
  background: #ffedd5;
  color: #9a3412;
}
.badge-missing {
  background: #fef3c7;
  color: #92400e;
}
.badge-blank {
  background: #e0f2fe;
  color: #075985;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Partially Failed — amber/orange */
.status-badge.status-partial {
  background: #fff3cd;
  color: #92400e;
  border: 1px solid #fbbf24;
}

/* Analysis Failed — red */
.status-badge.status-complete {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

/* Generic problematic — slate */
.status-badge.status-general {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.action-btn {
  background: #005eb8;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.loading-state,
.no-data-state {
  text-align: center;
  padding: 40px;
  color: #64748b;
}

.pagination-bar {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* Modal styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  padding: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.modal-header h4 {
  margin: 0;
  color: #005eb8;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.detail-row {
  margin-bottom: 12px;
  font-size: 14px;
  color: #333;
}

.modal-footer {
  text-align: right;
  margin-top: 20px;
}

.close-modal-btn {
  background: #64748b;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

/* Color Sorting & Controls Styles */
.breakdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.clear-filter-btn {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #005eb8;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
}

.clear-filter-btn:hover {
  background: #e2e8f0;
}

.breakdown-item.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.breakdown-item.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.breakdown-item.clickable.selected {
  border-color: #005eb8;
  background-color: #eff6ff;
}

.table-controls-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sort-by-select-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.sort-by-select-wrap label {
  font-weight: 500;
  color: #475569;
}

.sort-by-select-wrap select {
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
}

.sort-dir-toggle {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.sort-dir-toggle:hover {
  background: #e2e8f0;
}

.sortable-th {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable-th:hover {
  background-color: #e2e8f0;
}

.active-sort-th {
  background-color: #e0f2fe !important;
  color: #0284c7 !important;
}

.sort-arrow {
  margin-left: 4px;
  font-size: 11px;
}

.sort-arrow-placeholder {
  margin-left: 4px;
  font-size: 11px;
  opacity: 0.3;
}

.color-dots-legend {
  display: inline-flex;
  gap: 4px;
  margin-left: 8px;
  vertical-align: middle;
}

.color-dots-legend .dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.dot.red { background-color: #dc3545; }
.dot.orange { background-color: #fd7e14; }
.dot.yellow { background-color: #ffc107; }
.dot.blue { background-color: #17a2b8; }

</style>
